import { ApiError, api } from "@/lib/api";
import type { FiltrosAuditoria } from "@/lib/auditoria";
import { dispararDescarga } from "@/lib/descargas";

/**
 * MENSAJE_EXPORT_FALLIDO es el texto genérico de una exportación que no se pudo traer. Es la
 * ÚNICA declaración del literal en todo el SPA (#355, R5): las tres pantallas que exportan lo
 * consumen de aquí en vez de repetirlo, que es la deuda que #334/#346 cerraron.
 */
export const MENSAJE_EXPORT_FALLIDO = "No se pudo exportar.";

/**
 * mensajeDeErrorExport decide qué se le cuenta al usuario cuando falla una exportación
 * (#355, D1): un `ApiError` de estado 4xx es de NEGOCIO y su `message` está escrito para él
 * («acota los filtros: la exportación está limitada a 50000 filas»), así que se muestra tal
 * cual; un 5xx es infraestructura y su texto no le sirve de nada, así que cae al genérico. Lo
 * que no es un `ApiError` —red caída, respuesta malformada— tampoco tiene `message` de
 * servidor que enseñar (D2).
 */
export function mensajeDeErrorExport(err: unknown): string {
  if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
    // Un sobre tipado sin texto no puede dejar el aviso en blanco (convención del SPA,
    // `err.message || fallback`).
    return err.message || MENSAJE_EXPORT_FALLIDO;
  }
  return MENSAJE_EXPORT_FALLIDO;
}

/**
 * ExportTipo son los listados con exportación xlsx POR OBRA por la ruta genérica
 * `/export/{tipo}.xlsx`: los maestros de la #33 (áreas, equipos, candados; manual §8.2),
 * los LOTOs de la #60 (F5) y el resto del árbol de activos desde la #249 (fuentes, puntos y
 * procedimientos). Fuera de esta ruta quedan, por tener otro gate, las exportaciones de
 * PERSONAS de la obra (`descargarMiembros` / `descargarAutorizaciones`, RequireObraAdmin),
 * el informe general de la obra (`descargarReporteObra`) y el informe GLOBAL de usuarios
 * (`descargarUsuarios`, ADMIN, sin obra).
 */
export type ExportTipo =
  | "areas"
  | "equipos"
  | "fuentes"
  | "puntos"
  | "candados"
  | "procedimientos"
  | "lotos";

/**
 * NOMBRE_ARCHIVO es el nombre de RESPALDO de cada descarga: desde la #249 (D7) el nombre lo
 * fija el servidor en `Content-Disposition` y el SPA lo respeta; esto solo se usa si esa
 * cabecera falta o no es interpretable.
 */
const NOMBRE_ARCHIVO: Record<ExportTipo, string> = {
  areas: "areas.xlsx",
  equipos: "equipos.xlsx",
  fuentes: "fuentes.xlsx",
  puntos: "puntos.xlsx",
  candados: "candados.xlsx",
  procedimientos: "procedimientos.xlsx",
  lotos: "lotos.xlsx",
};

/**
 * FiltrosExport son los filtros de una exportación por entidad (#249, D4): EXACTAMENTE los
 * mismos parámetros que el listado de esa entidad (#247), con los mismos nombres en el API
 * y en la URL. Los campos vacíos/omitidos no viajan; los que no admita esa entidad los
 * ignora el parser del servidor.
 */
export type FiltrosExport = Record<string, string | undefined>;

/**
 * queryDeFiltros construye la query string (`?estado=…&desde=…`) a partir de los filtros de
 * exportación, omitiendo los campos vacíos. Devuelve cadena vacía si no hay ningún filtro.
 * Lo reutilizan los filtros de los listados (#247): el contrato de parámetros es el mismo
 * para consultar y para exportar, así que la serialización también debe serlo.
 */
export function queryDeFiltros(filtros?: Record<string, string | undefined>): string {
  if (!filtros) {
    return "";
  }
  const params = new URLSearchParams();
  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor != null && valor !== "") {
      params.set(clave, valor);
    }
  }
  const query = params.toString();
  return query === "" ? "" : `?${query}`;
}

/**
 * descargarMaestro pide GET /api/v1/obras/{obraId}/export/{tipo}.xlsx (LECTURA autenticada;
 * cualquier usuario onboarded) y fuerza la descarga en el navegador a partir del Blob
 * recibido. La exportación cuelga de la obra activa (#152): los listados se exportan por obra.
 *
 * `filtros` son los de la URL del listado y los acepta CUALQUIER tipo (#249, D4): el
 * servidor los interpreta con el mismo `ParseFiltros<Entidad>` que el `GET` de lista, así
 * que el libro trae exactamente lo que se está viendo. Desde #295 eso incluye a los LOTOs,
 * el último listado cuyo export tenía filtros propios (`estado` + rango de apertura, #214) y
 * que ahora comparte los NUEVE del índice (#247), con el mismo 400 FILTRO_INVALIDO.
 */
export async function descargarMaestro(
  tipo: ExportTipo,
  obraId: number,
  filtros?: FiltrosExport,
): Promise<void> {
  const { blob, nombreArchivo } = await api.getBlob(
    `/obras/${obraId}/export/${tipo}.xlsx${queryDeFiltros(filtros)}`,
  );
  dispararDescarga(blob, nombreArchivo ?? NOMBRE_ARCHIVO[tipo]);
}

/**
 * descargarLotosPDF pide GET /api/v1/obras/{obraId}/export/lotos.pdf (#238): el resumen tabular
 * en PDF de los LOTOs de la obra, con los MISMOS filtros que el xlsx — desde #295 (D2) los nueve
 * del índice, no solo estado + rango de apertura: las dos mitades del mismo botón descargan lo
 * que la pantalla muestra. Es una LECTURA autenticada abierta a cualquier usuario onboarded.
 * Fuerza la descarga a partir del Blob recibido; los filtros vacíos no viajan en la query.
 */
export async function descargarLotosPDF(obraId: number, filtros?: FiltrosExport): Promise<void> {
  const { blob, nombreArchivo } = await api.getBlob(
    `/obras/${obraId}/export/lotos.pdf${queryDeFiltros(filtros)}`,
  );
  dispararDescarga(blob, nombreArchivo ?? "lotos.pdf");
}

/**
 * descargarReporteLoto pide GET /api/v1/obras/{obraId}/lotos/{lotoId}/reporte.pdf (#213): el
 * reporte PDF de un LOTO con sus datos, cronología, candados y registro fotográfico. Es una
 * LECTURA abierta a cualquier miembro onboarded de la obra. `numeroLoto` nombra el archivo
 * (LOTO-2026-0001.pdf); si falta, se usa el id como respaldo.
 */
export async function descargarReporteLoto(
  obraId: number,
  lotoId: number,
  numeroLoto?: string,
): Promise<void> {
  const { blob, nombreArchivo } = await api.getBlob(`/obras/${obraId}/lotos/${lotoId}/reporte.pdf`);
  const nombre = numeroLoto && numeroLoto.trim() !== "" ? numeroLoto : `loto-${lotoId}`;
  dispararDescarga(blob, nombreArchivo ?? `${nombre}.pdf`);
}

/**
 * descargarMiembros pide GET /api/v1/obras/{obraId}/export/miembros.xlsx (#249, D3): los
 * miembros de la obra con su rol y su autorización LOTO. Lleva DATOS PERSONALES, así que en
 * el backend va bajo RequireObraAdmin —el mismo gate que su listado (#255 D2)—; el guard de
 * la pantalla es solo UX.
 */
export async function descargarMiembros(obraId: number): Promise<void> {
  const { blob, nombreArchivo } = await api.getBlob(`/obras/${obraId}/export/miembros.xlsx`);
  dispararDescarga(blob, nombreArchivo ?? "miembros.xlsx");
}

/**
 * descargarAutorizaciones pide GET /api/v1/obras/{obraId}/export/autorizaciones.xlsx
 * (#249, D3): las autorizaciones LOTO vivas de la obra. Mismo gate y mismo motivo que
 * `descargarMiembros`: son datos personales.
 */
export async function descargarAutorizaciones(obraId: number): Promise<void> {
  const { blob, nombreArchivo } = await api.getBlob(`/obras/${obraId}/export/autorizaciones.xlsx`);
  dispararDescarga(blob, nombreArchivo ?? "autorizaciones.xlsx");
}

/**
 * descargarReporteObra pide GET /api/v1/obras/{obraId}/export/reporte.xlsx (#249, D1): el
 * informe general de la obra, un único libro con una hoja por entidad. NO lleva datos
 * personales ni acepta filtros, así que lo descarga cualquier miembro onboarded.
 */
export async function descargarReporteObra(obraId: number): Promise<void> {
  const { blob, nombreArchivo } = await api.getBlob(`/obras/${obraId}/export/reporte.xlsx`);
  dispararDescarga(blob, nombreArchivo ?? "reporte.xlsx");
}

/**
 * descargarUsuarios pide GET /api/v1/export/usuarios.xlsx (#180): el informe GLOBAL de
 * usuarios (dos hojas: identidad global + membresías de todas las obras, #171). Es
 * ADMIN-only en el backend (RequireAdmin) y NO cuelga de ninguna obra, así que no recibe
 * `obraId` — pero sí `filtros`: acepta EXACTAMENTE los del listado `/usuarios` (#249, D4).
 * Fuerza la descarga a partir del Blob recibido.
 */
export async function descargarUsuarios(filtros?: FiltrosExport): Promise<void> {
  const { blob, nombreArchivo } = await api.getBlob(
    `/export/usuarios.xlsx${queryDeFiltros(filtros)}`,
  );
  dispararDescarga(blob, nombreArchivo ?? "usuarios.xlsx");
}

/**
 * descargarAuditoria pide GET /api/v1/auditoria/export.xlsx (#254) con los MISMOS filtros
 * que el visor y fuerza la descarga. Es ADMIN-only en el backend (RequireAdmin) y global
 * (el registro cruza todas las obras), así que no recibe `obraId`. Si el backend responde
 * 400 EXPORT_DEMASIADO_GRANDE, el ApiError sube al llamante para que la página pida acotar
 * los filtros en vez de descargar medio registro.
 */
export async function descargarAuditoria(filtros?: FiltrosAuditoria): Promise<void> {
  const { blob, nombreArchivo } = await api.getBlob(
    `/auditoria/export.xlsx${queryDeFiltros(filtros)}`,
  );
  dispararDescarga(blob, nombreArchivo ?? "auditoria.xlsx");
}
